import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** The open dialogs holding the page scroll lock, so overlapping dialogs share one lock and one scroll position. */
const scrollLockOwners = new Set();
let lockedScrollY = 0;

/**
 * Pins the page where it is while a dialog is open.
 * @param {Element} owner - The dialog component taking the lock.
 */
function lockPageScroll(owner) {
  if (scrollLockOwners.size === 0) {
    lockedScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
  }

  scrollLockOwners.add(owner);
}

/**
 * Releases the owner's lock, and restores the page and its scroll position when no other dialog holds one.
 * @param {Element} owner - The dialog component releasing the lock.
 */
function unlockPageScroll(owner) {
  if (!scrollLockOwners.delete(owner) || scrollLockOwners.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** Whether `closeDialog` is running its closing animation. */
  #closing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // `cancel` and `close` don't bubble, so they're caught on the way down to the dialog
    this.addEventListener('cancel', this.#handleCancel, { capture: true });
    this.addEventListener('close', this.#handleNativeClose, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('cancel', this.#handleCancel, { capture: true });
    this.removeEventListener('close', this.#handleNativeClose, { capture: true });
    unlockPageScroll(this);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    lockPageScroll(this);

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#closing) return;

    this.#closing = true;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    try {
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    } finally {
      this.#closing = false;
    }

    // The browser may have closed the dialog during the animation, and cleaned up already
    if (!dialog.open) {
      dialog.classList.remove('dialog-closing');
      return;
    }

    unlockPageScroll(this);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Runs the animated close for Escape and the Android back gesture, when the browser lets the close be cancelled.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * The browser closes the dialog by itself when it won't let the close be cancelled, e.g. a back gesture without a
   * prior tap, which used to leave the page pinned and unscrollable.
   *
   * @param {Event} event - The close event.
   */
  #handleNativeClose = (event) => {
    if (event.target !== this.refs.dialog || !scrollLockOwners.has(this)) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog.classList.remove('dialog-closing');
    unlockPageScroll(this);

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
